import { useAuth } from '@/composables/useAuth'
import { getControlCenterAccessSync } from '@/composables/useControlCenter'
import { canAccessDcgEquipment, canManageUsers, isAdminRole } from '@/composables/useAccessControl'

const { clearAuth } = useAuth()

// Routes that require Control Center access.
// 'subscriptions' left this list 2026-10-10: every customer role may open
// Billing — staff can buy for their own location and manage what they
// bought — and /api/billing/* scopes rows per user (BillingManageScope).
const CONTROL_CENTER_ROUTES = [
  'control-center-store-details',
  'insights-equipment-management',
  'control-center-platform-invites',
]

// Admin-only Control Center pages, backed by admin-middleware APIs —
// gated with the same admin-role check as the 'admin' route prefix below.
// User Management + Access Control moved here from CONTROL_CENTER_ROUTES
// (2026-08-10): managers manage their people on the Users page now, and
// these two pages' APIs are admin-middleware anyway — a manager reaching
// them by URL only got 403s.
const ADMIN_GUARDED_ROUTES = [
  'control-center-organizations',
  'control-center-roles-permissions',
]

const ADMIN_ROUTE_PREFIX = 'admin'

// Role lockdown (2026-08-11): the nav and every admin/manager UI check read
// the role cached in localStorage at LOGIN time (useAccessControl.getRoleId)
// — so a DB-side role change (demotion, or an invalid hand-edited value)
// kept the old UI shell alive until re-login, even though the backend APIs
// fail closed and 403'd all along. Every guarded navigation re-syncs the
// cached role from check-session's live answer and rebuilds the app when it
// changed. Returns true when a reload was triggered (caller stops routing).
function syncRoleFromSession(data) {
  const serverRoleId = Number(data?.userData?.['role_id'])

  if (!Number.isFinite(serverRoleId)) return false

  try {
    const raw = localStorage.getItem('userData')
    if (!raw || raw === 'undefined') return false

    const user = JSON.parse(raw)
    const storedRoleId = Number(user?.role?.id ?? user?.role_id ?? NaN)
    const serverCanManage = Boolean(data?.userData?.['can_manage_users'])
    const storedCanManage = Boolean(user?.['can_manage_users'])

    if (storedRoleId === serverRoleId && storedCanManage === serverCanManage) return false

    // Keep localStorage aligned with the server truth every time.
    if (user.role && typeof user.role === 'object') user.role.id = serverRoleId
    user['role_id'] = serverRoleId
    user['can_manage_users'] = serverCanManage
    localStorage.setItem('userData', JSON.stringify(user))

    // Reload AT MOST ONCE per distinct server state. The reload rebuilds the
    // nav/layout (they snapshot the role at mount, so an in-place update
    // can't demote an already-rendered admin shell). But another endpoint
    // (e.g. the layout's /api/users/info write) can re-persist userData
    // without these fields right after — which used to re-trip the mismatch
    // and reload forever. The per-signature guard reloads once for a given
    // (role, flag) pair, then leaves localStorage silently in sync: worst
    // case the nav is briefly stale until the next full load, never a loop.
    const signature = `${serverRoleId}:${serverCanManage ? 1 : 0}`

    if (sessionStorage.getItem('roleSyncReloaded') === signature) return false

    sessionStorage.setItem('roleSyncReloaded', signature)
    window.location.reload()

    return true
  } catch {
    return false // localStorage unavailable — backend gates still fail closed
  }
}

export const setupGuards = router => {
  router.beforeEach(async (to, from, next) => {
    const isPublic    = to.matched.some(r => r.meta.public)
    const isGuestOnly = to.matched.some(r => r.meta.guestOnly || r.meta.unauthenticatedOnly)

    // 1️⃣ public routes → always allow
    if (isPublic) {
      return next()
    }

    // 2️⃣ guest-only pages → check if user is already authenticated
    if (isGuestOnly) {
      try {
        const response = await fetch('/api/auth/check-session', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        })

        if (response.ok) {
          const data = await response.json()
          if (data.authenticated) {
            return next({ name: 'home' })
          }
        }
      } catch (e) {
        console.debug('Session check failed:', e)
      }

      return next()
    }

    // 3️⃣ Protected routes — verify session
    try {
      const response = await fetch('/api/auth/check-session', {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      })

      if (response.ok) {
        const data = await response.json()
        if (data.authenticated) {
          // Live-role sync — a changed/demoted role rebuilds the app before
          // any stale admin UI can render (see syncRoleFromSession above).
          if (syncRoleFromSession(data)) {
            return next(false)
          }

          // RightBytes admins have no use for the app launcher — send them
          // straight to the platform console (2026-08-14). Runs after the
          // role sync so a just-demoted admin doesn't get bounced here.
          if (to.name === 'home' && isAdminRole()) {
            return next({ name: 'control-center-organizations' })
          }

          // Block Control Center routes for unauthorized roles
          if (CONTROL_CENTER_ROUTES.includes(to.name) && !getControlCenterAccessSync().hasAccess) {
            return next({ name: 'home' })
          }

          // Users page is feature-flagged per manager (can_manage_users on
          // their users row) — backend mirror: invite.access middleware.
          if (to.name === 'control-center-platform-invites' && !canManageUsers()) {
            return next({ name: 'home' })
          }

          // Equipment (DCG) — RightBytes Admin and Support only.
          // Backend mirror: the dcg.equipment middleware.
          if (to.name === 'equipment' && !canAccessDcgEquipment()) {
            return next({ name: 'home' })
          }

          // Block admin-only Control Center pages for non-admin roles
          if (ADMIN_GUARDED_ROUTES.includes(to.name) && !isAdminRole()) {
            return next({ name: 'home' })
          }

          // Block admin routes for non-admin roles
          if (to.name && to.name.startsWith(ADMIN_ROUTE_PREFIX) && !isAdminRole()) {
            return next({ name: 'home' })
          }

          return next()
        }
      }

      console.warn('[Router Guard] Session check failed, redirecting to login')
      clearAuth()

      return next({
        name: 'login',
        query: { to: to.fullPath !== '/' ? to.fullPath : undefined },
      })

    } catch (error) {
      console.error('[Router Guard] Session check error:', error)
      clearAuth()

      return next({
        name: 'login',
        query: { to: to.fullPath !== '/' ? to.fullPath : undefined },
      })
    }
  })
}
